import React, { useEffect, useRef, useLayoutEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";

import { PaymentSystem } from "@web-solutions/react-billing";

import Analytics from "@web-solutions/module-analytics";

import { Progress } from "@web-solutions/base-app/components/upsales";

import { selectPaymentSystem } from "core/store/billing/selectors";

import { ProductDetails } from "core/store/billing/selectors";

import { Modal } from "core/ui-elements";
import { type Subscription } from "core/interfaces/billing";
import type { PaymentModalTitle } from "core/interfaces/payment-modal";

import { Options } from "../payment-processor-provider/components/payment-processor";

import BasePaymentModalBody from "./body";
import BasePaddleModalBody from "./paddle";

import { ReactComponent as IconClose } from "./icons/close-icon.svg";

import classes from "./style.module.scss";
import "./style.scss";
import { useRemoteConfig } from "core/hooks/use-remote-config";
import { PaymentError } from "core/interfaces/errors";
import { useStore } from "../../../../apps/virtual-brainary/src/onboarding/store/store";

interface Props {
  activeProduct: ProductDetails;
  onClose: () => void;
  onSuccess: (purchase: Subscription) => void;
  onError: (error: any) => void;
  onSubmit: (formData: any) => Promise<void>;
  showPopup: boolean;
  options: Options;
  showApplePay: boolean;
  showPayPal: boolean;
  showGooglePay: boolean;
  showMercadoPago?: boolean;
  onApplePayClick: () => void;
  onApplePaySubmit: (formData: any) => Promise<void>;
  onGooglePayClick: () => void;
  onGooglePaySubmit: (formData: any) => Promise<void>;
  onPayPalSubmit: (formData: any) => Promise<void>;
  onPayPalSuccess: (p: Subscription) => void;
  onPayPalError: (e: any) => void;
  onUserInfoChange: (info: any) => void;
  isOnlyBaseModal?: boolean;
  fullMode?: boolean;
  title?: PaymentModalTitle;
  subtitle?: React.ReactNode;
  showCard?: boolean;
  isPurchaseProgress?: boolean;
  upsaleTrigger?: string;
  onSaveTokenOneTime?: (tokenId: string) => void;
  snowModalOncePopup?: boolean;
  subscriptionPageType?: any;
}

const BasePaymentModal: React.FC<Props> = ({
  activeProduct,
  onClose,
  onSuccess,
  onError,
  onSubmit,
  isOnlyBaseModal,
  showPopup,
  options,
  showApplePay,
  showPayPal,
  showGooglePay,
  showMercadoPago,
  showCard,
  onApplePayClick,
  onApplePaySubmit,
  onGooglePayClick,
  onGooglePaySubmit,
  onPayPalSubmit,
  onPayPalSuccess,
  onPayPalError,
  onUserInfoChange,
  fullMode,
  title,
  subtitle,
  isPurchaseProgress,
  upsaleTrigger,
  onSaveTokenOneTime,
  snowModalOncePopup,
  subscriptionPageType,
}) => {
  const paymentSystem = useSelector(selectPaymentSystem);
  const isPending = useSelector(
      //@ts-ignore
    (state) => state.billing.pending || state.billing.orderPending
  );
  //@ts-ignore
  const isSubmitByCard = useSelector((state) => state.billing.isSubmitByCard);

  const currentPage = useStore((state) => state.currentPage);
  const isChangePaymentMethod = currentPage === "change-payment-method";

  const ref = useRef<HTMLDivElement>(null);
  const { newPaymentModal } = useRemoteConfig();
  useEffect(() => {
    if (isSubmitByCard) {
      ref.current?.scrollIntoView({
        behavior: "auto",
        block: "start",
        inline: "start",
      });
    }
  }, [isSubmitByCard]);

  useEffect(() => {
    if (showPopup) {
      Analytics.trackEvent("modal_payment", "open", {
        productId: activeProduct?.id,
        value: +activeProduct?.trialPriceAmount || +activeProduct?.amount,
        currency: activeProduct?.currency,
      });
      ref.current?.scrollIntoView(true);
    }
  }, [showPopup, activeProduct]);

  const handleApplePaySuccess = useCallback(
    (props: any) => {
      onSuccess(props);
    },
    [onSuccess]
  );

  const handleApplePayError = useCallback(
    (error: PaymentError) => {
      onError(error);
    },
    [onError]
  );

  useLayoutEffect(() => {
    if (showPopup) {
      document.body.classList.add("ReactModal__Body--open");
    } else {
      document.body.classList.remove("ReactModal__Body--open");
    }
  }, [showPopup]);

  return (
    <Modal
      isOpen
      className={classNames(
        classes.modalContainer,
        { [classes.fullMode]: fullMode },
        { [classes.modalContainer_active]: isPending },
        { [classes.hidden]: !showPopup }
      )}
      overlayClassName={classNames({ [classes.hidden]: !showPopup })}
    >
      {/*
        Подложка попапа белая при любой теме воронки, поэтому помечаем его
        светлой поверхностью: токены темы внутри возвращаются к светлым
        значениям (иначе в theme=dark текст и кнопка Apple Pay белые на белом).
      */}
      <div className={classes.innerContainer} ref={ref} data-theme="light">
        {!newPaymentModal && <br />}
        {!isChangePaymentMethod && (
          <button type="button" onClick={onClose} className={classes.close}>
            <IconClose width="24px" height="24px" />
          </button>
        )}

        {isPurchaseProgress && (
          <div className={classes.process}>
            <Progress currentStep={"order"} />
          </div>
        )}
        {paymentSystem === PaymentSystem.PADDLE ? (
          <BasePaddleModalBody
            subtitle={subtitle}
            activeProduct={activeProduct}
            options={options}
            onSubmit={onSubmit}
            onSuccess={onSuccess}
            onError={onError}
            onApplePayClick={onApplePayClick}
            onApplePaySubmit={onApplePaySubmit}
            onGooglePayClick={onGooglePayClick}
            onGooglePaySubmit={onGooglePaySubmit}
            onUserInfoChange={onUserInfoChange}
            onSaveTokenOneTime={onSaveTokenOneTime}
          />
        ) : (
          <BasePaymentModalBody
            title={title}
            subtitle={subtitle}
            activeProduct={activeProduct}
            options={options}
            showApplePay={showApplePay}
            showPayPal={showPayPal}
            showGooglePay={showGooglePay}
            showMercadoPago={showMercadoPago}
            upsaleTrigger={upsaleTrigger}
            isOnlyBaseModal={isOnlyBaseModal}
            showCard={showCard}
            onSubmit={onSubmit}
            onSuccess={onSuccess}
            onError={onError}
            onApplePayClick={onApplePayClick}
            onApplePaySubmit={onApplePaySubmit}
            onApplePaySuccess={handleApplePaySuccess}
            onApplePayError={handleApplePayError}
            onGooglePayClick={onGooglePayClick}
            onGooglePaySubmit={onGooglePaySubmit}
            onPayPalSubmit={onPayPalSubmit}
            onPayPalSuccess={onPayPalSuccess}
            onPayPalError={onPayPalError}
            onUserInfoChange={onUserInfoChange}
            onSaveTokenOneTime={onSaveTokenOneTime}
            showModalPopup={snowModalOncePopup}
            subscriptionPageType={subscriptionPageType}
          />
        )}
      </div>
    </Modal>
  );
};

export default BasePaymentModal;
